import {Loading, Tabs} from '@ds/components';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import type {MapLayerMouseEvent} from 'react-map-gl/maplibre';
import {useParams} from 'react-router-dom';
import {useAppDispatch, useAppSelector} from 'store';
import {
  disablePolygonDelivery,
  enablePolygonDelivery,
  getDeliveryCoverage,
  getOwnDeliveryCoverage
} from 'store/slices/services/thunk';

import AreaStatusCard from './components/AreaStatusCard/AreaStatusCard';
import ServiceAreaMap from './components/ServiceAreaMap/ServiceAreaMap';
import type {
  DeliveryCoverage,
  DeliveryMethod,
  SelectedArea,
  ServiceAreaPolygon
} from './types';

const TAB_ITEMS = [
  {key: 'own', label: 'پیک فروشگاه'},
  {key: 'express', label: 'پیک اسنپ'}
];

const ServiceArea: React.FC = () => {
  const dispatch = useAppDispatch();
  const {vendorId} = useParams();
  const {
    deliveryCoverageOwn,
    deliveryCoverageExpress,
    deliveryCoverageOwnError,
    deliveryCoverageExpressError,
    loadingDeliveryCoverage,
    enablingPolygonDelivery,
    disablingPolygonDelivery
  } = useAppSelector((state) => state.services);
  const [deliveryMethod, setDeliveryMethod] = useState<DeliveryMethod>('own');
  const [selectedArea, setSelectedArea] = useState<SelectedArea | null>(null);
  const [selectedPolygonId, setSelectedPolygonId] = useState<string | number | null>(null);
  const [polygons, setPolygons] = useState<ServiceAreaPolygon[]>([]);
  const [isComputingPolygons, setIsComputingPolygons] = useState(false);
  const workerRef = useRef<Worker | null>(null);
  const deliveryCoverageRef = useRef<DeliveryCoverage | undefined>();
  const deliveryMethodRef = useRef<DeliveryMethod>('own');

  const deliveryCoverage = useMemo(
    () =>
      (deliveryMethod === 'own'
        ? deliveryCoverageOwn
        : deliveryCoverageExpress) as DeliveryCoverage | undefined,
    [deliveryCoverageExpress, deliveryCoverageOwn, deliveryMethod]
  );

  const error =
    deliveryMethod === 'own' ? deliveryCoverageOwnError : deliveryCoverageExpressError;
  const isLoading =
    !error &&
    (loadingDeliveryCoverage ||
      isComputingPolygons ||
      enablingPolygonDelivery ||
      disablingPolygonDelivery);

  useEffect(() => {
    const worker = new Worker(new URL('./utils.ts', import.meta.url), {type: 'module'});
    worker.onmessage = (event: MessageEvent<ServiceAreaPolygon[]>) => {
      setPolygons(event.data ?? []);
      setIsComputingPolygons(false);
    };
    workerRef.current = worker;

    return () => {
      worker.terminate();
      workerRef.current = null;
    };
  }, []);

  useEffect(() => {
    deliveryCoverageRef.current = deliveryCoverage;
  }, [deliveryCoverage]);

  useEffect(() => {
    deliveryMethodRef.current = deliveryMethod;
  }, [deliveryMethod]);

  useEffect(() => {
    const coveragePolygons = deliveryCoverage?.data?.polygon;
    if (!Array.isArray(coveragePolygons)) {
      setPolygons([]);
      setIsComputingPolygons(false);
      return;
    }

    setIsComputingPolygons(true);
    workerRef.current?.postMessage({polygon: coveragePolygons});
  }, [deliveryCoverage]);

  useEffect(() => {
    if (!vendorId) return;

    void dispatch(getOwnDeliveryCoverage({vendor_id: vendorId}));
    void dispatch(
      getDeliveryCoverage({
        vendor_id: vendorId,
        delivery_method: 'express',
        is_enabled: true
      })
    );
  }, [dispatch, vendorId]);

  const clearSelection = useCallback(() => {
    setSelectedArea(null);
    setSelectedPolygonId(null);
  }, []);

  const handleMapClick = useCallback((event: MapLayerMouseEvent) => {
    if (deliveryMethodRef.current === 'express') return;

    const feature = event.features?.[0];
    if (!feature) {
      clearSelection();
      return;
    }

    const areaId = feature.properties?.id as string | number | undefined;
    const area = deliveryCoverageRef.current?.data?.polygon?.find(
      (item) => item.polygon_id.toString() === areaId?.toString()
    );

    if (area) {
      setSelectedArea({id: area.polygon_id, isActivated: area.is_enabled});
      setSelectedPolygonId(area.polygon_id);
    }
  }, [clearSelection]);

  const handleMapMouseEnter = useCallback((event: MapLayerMouseEvent) => {
    if (deliveryMethodRef.current === 'own') {
      event.target.getCanvas().style.cursor = 'pointer';
    }
  }, []);

  const handleMapMouseLeave = useCallback((event: MapLayerMouseEvent) => {
    event.target.getCanvas().style.cursor = '';
  }, []);

  const handleDeliveryMethodChange = (key: string) => {
    setDeliveryMethod(key as DeliveryMethod);
    clearSelection();
  };

  const handleAreaStatusChange = () => {
    if (!vendorId || !selectedArea) return;

    const request = selectedArea.isActivated
      ? disablePolygonDelivery({polygonId: selectedArea.id, vendorId})
      : enablePolygonDelivery({polygonId: selectedArea.id, vendorId});

    void dispatch(request).then(() => {
      window.setTimeout(() => {
        void dispatch(getOwnDeliveryCoverage({vendor_id: vendorId}));
      }, 1000);
    });
  };

  if (!vendorId) return null;

  return (
    <main className="relative h-full overflow-hidden bg-gray-02">
      <div className="absolute inset-x-[10%] top-4 z-10 overflow-hidden rounded-full border border-gray-01 bg-gray-02 shadow-md">
        <Tabs
          items={TAB_ITEMS}
          activeKey={deliveryMethod}
          onChange={handleDeliveryMethodChange}
          fullWidth
          tabBarClassName="border-0 before:border-0"
        />
      </div>

      {isLoading && (
        <div className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-2 bg-white/60">
          <Loading size="medium" mId="1" />
          <span className="text-body-sm text-gray-08">در حال بارگذاری...</span>
        </div>
      )}

      {selectedArea && deliveryMethod === 'own' && (
        <AreaStatusCard
          isActive={selectedArea.isActivated}
          onChange={handleAreaStatusChange}
          onClose={clearSelection}
        />
      )}

      {error ? (
        <p className="absolute inset-x-4 top-1/2 z-10 -translate-y-1/2 text-center text-body-sm-emphasis text-destructive">
          {error}
        </p>
      ) : (
        <ServiceAreaMap
          center={deliveryCoverage?.data?.centroid}
          deliveryMethod={deliveryMethod}
          polygons={polygons}
          selectedPolygonId={selectedPolygonId}
          onClick={handleMapClick}
          onMouseEnter={handleMapMouseEnter}
          onMouseLeave={handleMapMouseLeave}
        />
      )}
    </main>
  );
};

export default ServiceArea;
